import { z } from 'zod';
/**
 * Contrato del panel del cuidador · C-02, C-03, C-04.
 *
 * **El scope es restrictivo y está confirmado.** El cuidador acompaña: no ve
 * datos clínicos, no ve escalas, no ve el semáforo y no ve las conversaciones
 * del paciente con PIA (matriz de permisos de E2.4 — `paciente:acompanar` y
 * `contenido:ver`, nada más; `semaforos-etiquetas.md` §1 S3, redefinido por
 * Fundación el 06/08). Este contrato es la forma de que eso no dependa de que
 * una pantalla se acuerde: **lo que no está acá no puede mostrarse**.
 *
 * Lo que sí ve: si el paciente viene respondiendo, y el día del programa. Eso
 * es. Y desde el 06/08, tampoco recibe indicaciones — "no le indica qué hacer;
 * sugerir consulta médica queda a su criterio".
 *
 * **C-05 no se construye.** Era la variante "en alerta" del C-02 y decía "el
 * equipo médico está atendiendo una situación". No hay alertas y el equipo no
 * recibe avisos automáticos: esa pantalla le prometería al cuidador una
 * intervención que el sistema no dispara.
 */
/**
 * Cómo viene respondiendo el paciente.
 *
 * Tres valores operativos y **ningún color clínico**: `al-dia` es que hubo
 * contacto reciente, `sin-contacto-reciente` que hace varios días que no, y
 * `sin-datos` que el programa todavía no arrancó. No dice cómo está el paciente
 * — dice si está apareciendo, que es lo único que el cuidador puede saber.
 */
export const respuestaDelPacienteSchema = z.enum(['al-dia', 'sin-contacto-reciente', 'sin-datos']);
/**
 * Una consulta abierta del equipo · el protocolo de silencio, nivel 2.
 *
 * Cuando PIA insistió y el paciente no volvió, el nivel 2 le escribe a la
 * persona de contacto. Esa salida es el `silence.retried` de nivel 2, y su id de
 * evento **es** el id de la consulta: no hace falta una tabla nueva, y la
 * respuesta queda atada al contacto exacto que la originó.
 */
export const consultaAbiertaSchema = z.strictObject({
    consultaId: z.uuid(),
    enviadaAt: z.iso.datetime(),
    diasSinContacto: z.number().int().nonnegative(),
});
export const inicioDelCuidadorSchema = z.strictObject({
    programaId: z.uuid(),
    /** Alias operativo del paciente. Nunca su nombre real ni su DNI. */
    pacienteAlias: z.string().max(80),
    /** Cómo se presenta el cuidador ante el paciente ("Hijo", "Vecina"). */
    vinculo: z.string().max(40),
    diaDelPrograma: z.number().int().nonnegative(),
    totalDeDias: z.number().int().positive(),
    respondiendo: respuestaDelPacienteSchema,
    ultimaInteraccionAt: z.iso.datetime().nullable(),
    /** `null` mientras el equipo no le haya preguntado nada. */
    consulta: consultaAbiertaSchema.nullable(),
});
/**
 * Las tres opciones de C-03.
 *
 * Cerradas a propósito: es lo que hace que la respuesta sea un evento
 * estructurado y no una narrativa que alguien tenga que interpretar. El
 * wireframe ofrece además un "¿querés agregar algo?" libre — no entra, por lo
 * mismo que el texto libre del reporte del paciente: el payload de
 * `caregiver.response_recorded` es cerrado, y un campo cuyo contenido no llega a
 * ningún lado es peor que no tenerlo.
 */
export const OPCIONES_DEL_CUIDADOR = ['esta-bien', 'necesita-ayuda', 'no-pude-contactar'];
export const opcionDelCuidadorSchema = z.enum(OPCIONES_DEL_CUIDADOR);
export const responderConsultaSchema = z.strictObject({
    consultaId: z.uuid(),
    opcion: opcionDelCuidadorSchema,
});
export const respuestaRegistradaSchema = z.strictObject({
    eventoId: z.uuid(),
    opcion: opcionDelCuidadorSchema,
    occurredAt: z.iso.datetime(),
});
//# sourceMappingURL=cuidador.js.map